import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useLocation, useNavigate } from 'react-router-dom'
import { Form, Divider, Col } from 'antd'
import { useQuery } from '@apollo/client'
import { pathOr, isEmpty, isNil } from 'ramda'
import { Button, Select, TextArea } from 'ui/styled'
import Appointment from 'model/appointments/appointment'
import CostEstimationModal from 'components/CostEstimationModal'
import AttachEntityModal from 'components/AttachEntityModal'
import { unSnake } from 'utils/strings'
import { ZIP_CODE_MAX_LENGTH, ZIP_CODE } from 'constants/constants'
import NumberInput from 'components/NumberInput/NumberInput'
import { Label } from 'pages/Ticket/styled'
import { getNumberRules } from 'utils/numberHelper'
import PLANS_WITH_BENEFITS from 'apollo/queries/PLANS_WITH_BENEFITS'
import useAppointmentUpdate from 'apollo/mutations/APPOINTMENT_UPDATE'
import TimeSlots from './AppointmentFields/TimeSlots'
import TicketStatus from '../TicketStatus'
import { StyledRow, StyledCol } from './styled'
import { Ticket_Status_Code } from 'generated/types'

const FormItem = Form.Item

const categories = ['medical_care', 'dental_care', 'vision_care']

const AppointmentFields = ({
  memberId,
  appointment = new Appointment({ id: '' }),
  assignedEntity = {},
  costEstimation = {},
  timeSlots = [],
  locationUuid = false,
  onSaveEntity = () => {},
  onSaveCostEstimation = () => {},
  onSaveTimeSlots = () => {},
  onSaveLocationUuid = () => {},
  onSaveFirstTime = () => {},
  onSaveRequestNotes = () => {},
  onSavePreferredZip = () => {},
  setFieldsValue = () => {},
  isForm = false
}) => {
  const [showCostEstimationModal, setShowCostEstimationModal] = useState(false)
  const [showAttachEntityModal, setShowAttachEntityModal] = useState(false)
  const [selectedEntity, setSelectedEntity] = useState(assignedEntity)

  const { pathname } = useLocation()
  const navigate = useNavigate()

  const onCloseModal = () => {
    navigate(pathname)
    setShowAttachEntityModal(false)
  }

  const { onAppointmentUpdate } = useAppointmentUpdate()

  // Cost estimation modal can access this cached data later
  // instead of requesting it from the server
  const { loading } = useQuery(PLANS_WITH_BENEFITS, {
    variables: {
      filters: {
        member_id: memberId,
        categories,
        eligibility_status: 'current'
      }
    },
    skip: !memberId,
    fetchPolicy: 'cache-first'
  })

  if (!memberId) return null
  const isProvider = selectedEntity?.isProvider
  const isFacility = selectedEntity?.isFacility

  const entityData = {
    cost: costEstimation.cost,
    cost_copay: costEstimation.cost_copay,
    cost_coinsurance: costEstimation.cost_coinsurance,
    cost_deductible: costEstimation.cost_deductible,
    cost_not_covered: costEstimation.cost_not_covered,
    insurance_plan: costEstimation.insurance_plan,
    preferred_insurance_benefit_tier_name:
      costEstimation.preferred_insurance_benefit_tier_name,
    insurance_tier_id: costEstimation.insurance_tier_id,
    ...(isProvider && {
      full_name: selectedEntity.full_name,
      addresses: selectedEntity.addresses
    }),
    ...(isFacility && { name: selectedEntity.name })
  }

  const entityAddresses = pathOr([], ['addresses'], selectedEntity)
  const entityContacts = pathOr({}, ['contacts'], selectedEntity)
  const saved_address = appointment?.saved_address

  // saved_address is needed to populate the dropdown in case
  // the address used before gets deleted
  let providerAddresses = []
  if (isProvider) {
    providerAddresses = entityAddresses.map(address => ({
      label: `${address.address}${address.phone ? ` / ${address.phone}` : ''}`,
      value: address.uuid
    }))
    if (
      saved_address &&
      saved_address?.uuid &&
      !entityAddresses.find(address => address.uuid === saved_address.uuid)
    )
      providerAddresses.push({
        label: `${saved_address.address}${
          saved_address.phone ? ` / ${saved_address.phone}` : ''
        }`,
        value: saved_address.uuid,
        disabled: true
      })
  }

  const locationOptions = isProvider
    ? providerAddresses
    : [
        {
          label: `${entityContacts.address}${
            entityContacts.phone ? ` / ${entityContacts.phone}` : ''
          }`,
          value: selectedEntity?.uuid || entityContacts?.uuid
        }
      ]

  const saveCostEstimation = async values => {
    const { isErrorFree } = await onSaveCostEstimation(values)
    if (isErrorFree) setShowCostEstimationModal(false)
  }

  const getDefaultLocation = entity => {
    let result
    if (entity.isProvider)
      result = pathOr(undefined, ['addresses', 0, 'uuid'], entity)
    if (entity.isFacility)
      result = pathOr(undefined, ['contacts', 'uuid'], entity)

    return result
  }

  const saveSelectedEntity = async selected => {
    const location = getDefaultLocation(selected)
    let success
    if (appointment.id) {
      // Need to update entity and its location simultaneously
      success = await onSaveEntity(selected, location)
    } else {
      if (selected.isFacility) onSaveLocationUuid(location)
      else if (selectedEntity !== selected) onSaveLocationUuid(null)

      success = await onSaveEntity(selected)
    }
    if (success?.isErrorFree !== false) {
      if (isForm) setFieldsValue({ preferred_entity: selected })
      setSelectedEntity(selected)
    }
  }

  const renderEntityInfo = () => {
    let specialty
    let phone
    let selectedAddress

    const errorLabel = `Data error, entity ID: ${selectedEntity.id}`
    let isValidEntity = true

    if (locationUuid && selectedEntity) {
      if (isProvider) {
        isValidEntity = selectedEntity.id && selectedEntity.full_name
        selectedAddress = selectedEntity.addresses.find(
          address => address.uuid === locationUuid
        )
        phone = pathOr(null, ['phone'], selectedAddress)
      }
      if (isFacility) {
        isValidEntity = selectedEntity.uuid && selectedEntity.name
        phone = pathOr(null, ['contacts', 'phone'], selectedEntity)
      }
    }

    if (isProvider) {
      specialty = pathOr(false, ['specialties', 0, 'name'], selectedEntity)
      return (
        <>
          <Label font="bold">
            {`${
              isValidEntity ? selectedEntity.full_name : errorLabel
            } (Provider) ${specialty ? `(${specialty})` : ''}`}
          </Label>
          {phone && <Label font="bold">{phone}</Label>}
        </>
      )
    }
    if (isFacility) {
      specialty = pathOr(false, ['display_location_types', 0], selectedEntity)
      return (
        <>
          <Label font="bold">
            {`${isValidEntity ? selectedEntity.name : errorLabel} (Facility) ${
              specialty ? `(${unSnake(specialty)})` : ''
            }`}
          </Label>
          {phone && <Label font="bold">{phone}</Label>}
        </>
      )
    }
    return null
  }

  const preferredAddress = pathOr(null, ['preferred_address'], appointment)
  const shouldBlockUI = pathOr(false, ['shouldBlockUI'], appointment)
  const ticketState = pathOr({}, ['ticket', 'state'], appointment)

  const shouldBlockArchiveBtn = ticketState.code === Ticket_Status_Code.Closed
  const shouldBlockUnarchiveBtn =
    ticketState.code !== Ticket_Status_Code.InProgress

  const archiveAppointment = async () => {
    await onAppointmentUpdate({
      id: appointment.id,
      attributes: {
        archived: !appointment.archived
      }
    })
  }

  return (
    <>
      <Divider />
      <StyledRow gutter={16}>
        <Col span={12}>
          <Label required>Provider/Facility</Label>
          <FormItem
            name="preferred_entity"
            rules={[
              {
                required: true,
                validator: () => {
                  if (Object.keys(selectedEntity).length)
                    return Promise.resolve()
                  return Promise.reject(
                    new Error('Please select a Provider/Facility')
                  )
                }
              }
            ]}
          >
            {!!selectedEntity && renderEntityInfo()}
          </FormItem>
        </Col>
        <StyledCol span={12}>
          <Button
            onClick={() => setShowAttachEntityModal(true)}
            btnsize="custom"
            height="32px"
            data-test="ticketAttachProviderFacilityBtn"
            disabled={shouldBlockUI}
          >
            Find/Attach Provider or Facility
          </Button>
        </StyledCol>
      </StyledRow>
      {appointment.id && (
        <StyledRow gutter={16}>
          <Col span={12}>
            <Label>Insurance / Cost Estimation Data</Label>
            {isEmpty(costEstimation) && (
              <Label font="bold">This appointment need a cost estimate</Label>
            )}
            {!isEmpty(costEstimation) && (
              <Label font="bold">
                {`This appointment is expected to cost the consumer $${costEstimation.cost_not_covered}`}
              </Label>
            )}
          </Col>
          <StyledCol span={12}>
            <Button
              onClick={() => setShowCostEstimationModal(true)}
              btnsize="custom"
              height="32px"
              disabled={isEmpty(selectedEntity) || shouldBlockUI}
              data-test="ticketEditEstimatesBtn"
            >
              Edit Insurance & Cost Estimates
            </Button>
          </StyledCol>
        </StyledRow>
      )}
      {!isEmpty(selectedEntity) &&
        !isNil(selectedEntity) &&
        locationUuid !== false && (
          <StyledRow gutter={16} key={locationUuid}>
            <Col span={24}>
              <Label>Provider/Facility Location</Label>
              <FormItem
                name="location_uuid"
                rules={[
                  {
                    required: true,
                    message: 'Please choose the location'
                  }
                ]}
              >
                <Select
                  options={locationOptions}
                  onChange={onSaveLocationUuid}
                  disabled={shouldBlockUI}
                  data-test="ticketSelectLocation"
                />
              </FormItem>
            </Col>
          </StyledRow>
        )}
      <StyledRow gutter={16}>
        <Col span={24}>
          <Label>Reason for Visit</Label>
          <FormItem name="request_notes">
            <TextArea
              disabled={shouldBlockUI}
              onChange={e => onSaveRequestNotes(e.target.value)}
              data-test="ticketRequestNotes"
            />
          </FormItem>
        </Col>
      </StyledRow>
      <StyledRow gutter={16}>
        <Col span={12}>
          <Label required>Relationship with Provider / Facility</Label>
          <FormItem
            name="first_time_visit"
            rules={[
              {
                required: true,
                message: 'Please select a relationship with provider / facility'
              }
            ]}
          >
            <Select
              options={[
                { label: 'New', value: true },
                { label: 'Returning', value: false }
              ]}
              onChange={onSaveFirstTime}
              disabled={shouldBlockUI}
              data-test="ticketSelectRelationship"
            />
          </FormItem>
        </Col>
        <Col span={12}>
          <Label required>Location Preference (ZIP code)</Label>
          <FormItem
            name="preferred_location_zip"
            rules={getNumberRules({
              maxLength: ZIP_CODE_MAX_LENGTH,
              type: ZIP_CODE,
              isRequired: true
            })}
          >
            <NumberInput
              type="zip"
              initialValue={preferredAddress}
              maxLength={ZIP_CODE_MAX_LENGTH}
              onChange={onSavePreferredZip}
              disabled={shouldBlockUI}
            />
          </FormItem>
        </Col>
      </StyledRow>
      <FormItem
        name="preferred_timeslots"
        rules={[
          {
            required: true,
            validator: () => {
              if (timeSlots.length) return Promise.resolve()
              return Promise.reject(new Error('Please select a timeslot'))
            }
          }
        ]}
      >
        <TimeSlots
          appointment={appointment}
          timeSlots={timeSlots}
          setTimeSlots={onSaveTimeSlots}
          locationUuid={locationUuid}
          setFieldsValue={setFieldsValue}
          isForm={isForm}
        />
      </FormItem>
      {appointment.id && (
        <TicketStatus
          gutter={16}
          rowStyle={{ padding: '8px 0' }}
          isAppointment
          status={
            <Col span={12}>
              <Label>{appointment.displayStatusText}</Label>
            </Col>
          }
          archiveSpan={12}
          archiveStyle={{ display: 'flex', justify: 'flex-end' }}
          archived={appointment.archived}
          archiveItem={archiveAppointment}
          shouldBlockArchiveBtn={shouldBlockArchiveBtn}
          shouldBlockUnarchiveBtn={shouldBlockUnarchiveBtn}
        />
      )}
      <AttachEntityModal
        visible={showAttachEntityModal}
        memberId={memberId}
        onCancel={onCloseModal}
        onSave={saveSelectedEntity}
      />
      <CostEstimationModal
        visible={showCostEstimationModal}
        member_id={memberId}
        isFacility={isFacility}
        ticketView
        entity={entityData}
        onCancel={() => setShowCostEstimationModal(false)}
        onSave={saveCostEstimation}
        loading={loading}
      />
    </>
  )
}

AppointmentFields.propTypes = {
  appointment: PropTypes.shape(Appointment.shape),
  memberId: PropTypes.string.isRequired,

  assignedEntity: PropTypes.object,
  costEstimation: PropTypes.object,
  timeSlots: PropTypes.array,
  setFieldsValue: PropTypes.func,
  isForm: PropTypes.bool,
  locationUuid: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),

  onSaveEntity: PropTypes.func,
  onSaveCostEstimation: PropTypes.func,
  onSaveTimeSlots: PropTypes.func,
  onSaveLocationUuid: PropTypes.func,
  onSaveFirstTime: PropTypes.func,
  onSaveRequestNotes: PropTypes.func,
  onSavePreferredZip: PropTypes.func
}

export default AppointmentFields
